import { ApiClientError } from "../api/client";
import type { AppI18n } from "@/i18n/context";
import { getApiErrorMessage } from "../presentation";
import {
  classifyRequestFailure,
  failureDetailKey,
  failureTitleKey,
  type RecoveryAction,
  type RequestFailureClassification,
} from "./request-failure";

export type PresentRequestFailureMode = "page" | "toast";

export type PresentedRequestFailure =
  | {
      description: string;
      kind: "specific";
    }
  | {
      classification: RequestFailureClassification;
      description: string;
      kind: "classified";
      plan: {
        primary: RecoveryAction | null;
        secondary: RecoveryAction | null;
      };
      requestId: string | null;
      titleKey: string;
      tone: "danger" | "warning";
    };

const resolveDescription = (
  error: unknown,
  classification: RequestFailureClassification,
  t: AppI18n["t"],
  fallback: string,
) => {
  if (error instanceof ApiClientError) {
    return getApiErrorMessage(error, fallback, t);
  }

  if (classification.messageKey) {
    return t(`apiErrors.${classification.messageKey}`);
  }

  return t(failureDetailKey(classification.kind));
};

/** 把分类结果转为页面或 Toast 可消费的展示结构。 */
export const presentRequestFailure = (
  error: unknown,
  mode: PresentRequestFailureMode,
  t: AppI18n["t"],
  fallback = t("errors.unavailableDetail"),
): PresentedRequestFailure => {
  const classification = classifyRequestFailure(error);

  if (classification.kind === "api") {
    return {
      description: resolveDescription(error, classification, t, fallback),
      kind: "specific",
    };
  }

  if (mode === "toast" && classification.kind === "network") {
    return {
      classification,
      description: t(failureDetailKey("network")),
      kind: "classified",
      plan: {
        primary: classification.primary,
        secondary: classification.secondary,
      },
      requestId: classification.requestId,
      titleKey: failureTitleKey("network"),
      tone: classification.tone,
    };
  }

  const titleKind = classification.kind;

  return {
    classification,
    description: resolveDescription(error, classification, t, fallback),
    kind: "classified",
    plan: {
      primary: classification.primary,
      secondary: classification.secondary,
    },
    requestId: classification.requestId,
    titleKey: failureTitleKey(titleKind),
    tone: classification.tone,
  };
};
